import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortingState } from '@cast/design-system';
import { CostReportAllocationGroupWorkload } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_WORKLOADS_QUERY_KEY = 'allocation-groups-workloads';

const getAllocationGroupWorkloadsQueryKey = ({
  groupId,
  startTime,
  endTime,
  clusterIds,
  useListingPrices,
  includeIdleCosts,
  sortingState,
}: Partial<UseAllocationGroupWorkloadsQueryProps> = {}) =>
  [
    ALLOCATION_GROUPS_WORKLOADS_QUERY_KEY,
    ...(groupId !== undefined
      ? [
          groupId,
          startTime,
          endTime,
          clusterIds,
          useListingPrices,
          includeIdleCosts,
          sortingState,
        ]
      : []),
  ] as const;

type UseAllocationGroupWorkloadsQuery = {
  allocationGroupWorkloads?: CostReportAllocationGroupWorkload[];
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  count: number;
};

type UseAllocationGroupWorkloadsQueryProps = {
  groupId: string;
  startTime: string;
  endTime: string;
  clusterIds: string[];
  useListingPrices: boolean;
  sortingState?: SortingState;
  includeIdleCosts?: boolean;
  pageLimit?: number;
};

const getAllocationGroupWorkloadsQueryOptions = ({
  groupId,
  startTime,
  endTime,
  clusterIds,
  useListingPrices,
  sortingState,
  includeIdleCosts = false,
  pageLimit = 50,
}: UseAllocationGroupWorkloadsQueryProps) =>
  infiniteQueryOptions({
    queryKey: getAllocationGroupWorkloadsQueryKey({
      groupId,
      startTime,
      endTime,
      clusterIds,
      useListingPrices,
      includeIdleCosts,
      sortingState,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getAllocationGroupWorkloads({
        groupId,
        clusterIds,
        startTime,
        endTime,
        useListingPrices,
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortingState?.columnId,
        sortOrder: sortingState?.sortBy?.direction,
        includeIdleResourceCosts: includeIdleCosts,
      });
      return data;
    },
    enabled: !!groupId,
    refetchOnWindowFocus: false,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useAllocationGroupWorkloadsQuery = (
  props: UseAllocationGroupWorkloadsQueryProps
): UseAllocationGroupWorkloadsQuery => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(getAllocationGroupWorkloadsQueryOptions(props));

  return {
    isLoading,
    allocationGroupWorkloads: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
  };
};
